// app/frontend/hooks/useScrollHeader.ts
import { useState, useEffect, useRef } from "react";

export interface UseScrollHeaderOptions {
  /**
   * Enable/disable the scroll header behavior
   * Default: false
   */
  enabled?: boolean;
  /**
   * Scroll distance threshold before triggering hide/show (in pixels)
   * Default: 20
   */
  scrollThreshold?: number;
  /**
   * Scroll velocity threshold (pixels per scroll event)
   * Default: 5
   */
  velocityThreshold?: number;
  /**
   * Delay in milliseconds before hiding header when scrolling down
   * Default: 150
   */
  hideDelay?: number;
  /**
   * Whether to always show header at top of page (scrollY = 0)
   * Default: true
   */
  alwaysShowAtTop?: boolean;
  /**
   * Breakpoint below which this behavior applies (mobile only)
   * Default: "sm" (640px)
   */
  mobileBreakpoint?: string;
}

/**
 * useScrollHeader - Custom hook for scroll-away header behavior
 *
 * Features:
 * - Hides header when scrolling down, shows when scrolling up
 * - Hybrid detection: threshold + velocity for smooth behavior
 * - Always shows at top of page
 * - Delay before hiding to prevent accidental toggles
 * - Mobile-only by default
 *
 * @example
 * const { headerVisible } = useScrollHeader({
 *   scrollThreshold: 20,
 *   hideDelay: 150
 * });
 */
export function useScrollHeader(options: UseScrollHeaderOptions = {}) {
  const {
    enabled = false,
    scrollThreshold = 20,
    velocityThreshold = 5,
    hideDelay = 150,
    alwaysShowAtTop = true,
    mobileBreakpoint = "sm",
  } = options;

  const [headerVisible, setHeaderVisible] = useState(true);
  
  // Mobile breakpoint values
  const breakpoints: Record<string, number> = {
    xs: 576,
    sm: 640,
    md: 768,
    lg: 1024,
  };
  const breakpointValue = breakpoints[mobileBreakpoint] || 640;
  
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpointValue;
  });
  
  const lastScrollY = useRef(0);
  const lastScrollTime = useRef(Date.now());
  const scrollVelocity = useRef(0);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ticking = useRef(false);

  // Check if we're on mobile (with resize listener)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpointValue);
    };

    // Re-check on mount to ensure we have the correct value
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [breakpointValue]);

  useEffect(() => {
    console.log("[useScrollHeader] enabled:", enabled);
    console.log("[useScrollHeader] initial isMobile:", isMobile);
    console.log("[useScrollHeader] breakpointValue:", breakpointValue);
    console.log("[useScrollHeader] window.innerWidth:", typeof window !== "undefined" ? window.innerWidth : "N/A");
    
    if (!enabled) {
      // Always show header if disabled
      setHeaderVisible(true);
      console.log("[useScrollHeader] Disabled - always showing header");
      return;
    }

    const handleScroll = () => {
      // Check mobile inside handler to handle dynamic changes
      const currentIsMobile = window.innerWidth < breakpointValue;
      
      if (!currentIsMobile) {
        // Always show header on desktop
        setHeaderVisible(true);
        return;
      }

      if (ticking.current) return;
      
      ticking.current = true;
      requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const currentTime = Date.now();
        const timeDelta = currentTime - lastScrollTime.current;
        const scrollDelta = Math.abs(currentScrollY - lastScrollY.current);
        
        // Calculate velocity (pixels per millisecond)
        if (timeDelta > 0) {
          scrollVelocity.current = scrollDelta / timeDelta;
        }

        // Always show at top
        if (alwaysShowAtTop && currentScrollY === 0) {
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
          }
          setHeaderVisible(true);
          lastScrollY.current = currentScrollY;
          lastScrollTime.current = currentTime;
          ticking.current = false;
          return;
        }

        // Determine scroll direction
        const scrollingDown = currentScrollY > lastScrollY.current;
        const scrollingUp = currentScrollY < lastScrollY.current;
        const scrollDistance = Math.abs(currentScrollY - lastScrollY.current);

        // Check if we've scrolled enough distance
        const passedThreshold = scrollDistance >= scrollThreshold;
        // Check if velocity is significant
        const passedVelocity = scrollVelocity.current * 1000 >= velocityThreshold;

        if (scrollingDown && passedThreshold && passedVelocity) {
          // Clear any pending show
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
          }
          
          // Add delay before hiding
          console.log("[useScrollHeader] Scrolling down - hiding header");
          hideTimeoutRef.current = setTimeout(() => {
            console.log("[useScrollHeader] Hiding header now");
            setHeaderVisible(false);
          }, hideDelay);
        } else if (scrollingUp && passedThreshold && passedVelocity) {
          // Clear hide timeout and show immediately
          console.log("[useScrollHeader] Scrolling up - showing header");
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
          }
          setHeaderVisible(true);
        }

        lastScrollY.current = currentScrollY;
        lastScrollTime.current = currentTime;
        ticking.current = false;
      });
    };

    // Set initial state based on current screen size and scroll position
    const initialIsMobile = window.innerWidth < breakpointValue;
    lastScrollY.current = window.scrollY;
    
    if (!initialIsMobile) {
      setHeaderVisible(true);
    } else if (alwaysShowAtTop && window.scrollY === 0) {
      // Always show at top
      setHeaderVisible(true);
    }

    // Trigger initial scroll check
    handleScroll();
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [enabled, scrollThreshold, velocityThreshold, hideDelay, alwaysShowAtTop, breakpointValue]);

  return { headerVisible };
}

